import { FC, useMemo } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { InputWithSuggestions, Suggestion } from '@telialt/common-input-with-suggestions/src';
import { SuggestionsProps } from '@telialt/common-input-with-suggestions/src/components/Suggestions/Suggestions';
import { normalizeString } from '@telialt/utils/src/helpers';
import { useComponentId } from '@telialt/utils/src/hooks';

import { Salon } from '../../model';
import { RetailsTestID } from '../../test-ids';
import { getPriorityCities } from '../../utils';
import { FieldLabel } from '../Retails.styles';
import { useRetailsContext } from '../RetailsContext';
import { SearchFieldContainer, SearchFieldWrapper, SuggestionsList, SuggestionsWrapper } from './CitySearch.styles';
import { NotFound } from './NotFound';
import { QuickLinks } from './QuickLinks';
import { SuggestionItem } from './SuggestionItem';

export const CitySearch: FC = () => {
    const { allCities, filteredCities, selectedCity, selectCity, selectSalon, isHiddenFromScreenReader } =
        useRetailsContext();
    const citySearchLabelId = useComponentId('city-search-label');

    const { t } = useTranslations();

    const quickLinkSuggestions = useMemo(
        () => getPriorityCities(allCities).map((city) => ({ label: city.name, value: city.id })),
        [allCities],
    );

    const handleSearchCities = async (keyword: string) => {
        return filteredCities
            .filter((option) => {
                return normalizeString(option.name).indexOf(normalizeString(keyword)) !== -1;
            })
            .map((city) => ({ label: city.name, value: city.id }));
    };

    const handleSuggestionSelect = (suggestion?: Suggestion) => {
        const city = filteredCities.find(({ id }) => id === suggestion?.value);
        selectCity(city);
    };

    const handleSalonSelect = (salon: Salon) => {
        selectSalon(salon);
    };

    const renderQuickLinks = (onSelect: (suggestion: Suggestion) => void, props: SuggestionsProps) => {
        return (
            <QuickLinks
                suggestions={quickLinkSuggestions}
                allCities={allCities}
                onSelect={onSelect}
                onSalonSelect={handleSalonSelect}
                {...props}
            />
        );
    };

    const renderSuggestions = (
        suggestions: Suggestion[],
        onSelect: (suggestion: Suggestion) => void,
        props: SuggestionsProps,
    ) => {
        return (
            <SuggestionsWrapper data-test={RetailsTestID.CITY_SEARCH_SUGGESTIONS}>
                <SuggestionsList role="listbox" id={props.id} aria-label={t['retails_map.search.results_label']}>
                    {suggestions.map((suggestion, index) => (
                        <SuggestionItem
                            key={`${suggestion.value}-${index}`}
                            label={suggestion.label}
                            count={allCities.find((city) => city.id === suggestion.value)?.count}
                            onClick={() => onSelect(suggestion)}
                        />
                    ))}
                </SuggestionsList>
            </SuggestionsWrapper>
        );
    };

    const value = useMemo(
        () => ({ label: selectedCity?.name || '', value: selectedCity?.id || '' }),
        [selectedCity?.id, selectedCity?.name],
    );

    return (
        <SearchFieldWrapper data-test={RetailsTestID.CITY_SEARCH}>
            <SearchFieldContainer>
                <FieldLabel tag="h2" variant="title-100" id={citySearchLabelId}>
                    {t['retails_map.search.label']}
                </FieldLabel>
                <InputWithSuggestions
                    variant="gray"
                    placeholder={t['retails_map.search.placeholder']}
                    minKeywordLength={1}
                    searchDebounceTime={10}
                    loadSuggestions={handleSearchCities}
                    customQuickLinksContent={quickLinkSuggestions.length > 0 ? renderQuickLinks : undefined}
                    noResultsContent={<NotFound />}
                    customSuggestionsContent={renderSuggestions}
                    shouldFetchOnValueChange={true}
                    value={value}
                    onSelect={handleSuggestionSelect}
                    disabled={isHiddenFromScreenReader}
                    ariaLabelledBy={citySearchLabelId}
                />
            </SearchFieldContainer>
        </SearchFieldWrapper>
    );
};
